import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

const NICHES = [
  "Real Estate Agents","Insurance Agents","Loan Consultants","Digital Marketing Agency",
  "Web Development Company","Interior Designers","Restaurants","Hospitals & Clinics",
  "Car Dealers","Gym & Fitness Centers","CA & Tax Consultants","Wedding Planners",
  "Travel Agencies","IT Companies","Schools & Coaching Centers","Builders & Contractors",
  "Solar Energy Companies","Pharma Companies","Export Companies","Event Planners",
  "Beauty Parlours & Salons","Dentists","Architects","Lawyers & Advocates",
  "Pest Control Services","Packers & Movers","Catering Services","Photography Studios",
  "Jewellery Shops","Hardware Stores","Auto Spare Parts","Textile Manufacturers",
  "Hotels & Resorts","Coaching Centers","Diagnostic Labs","Physiotherapy Clinics",
];
const CITIES = [
  "Delhi","Mumbai","Bangalore","Chennai","Hyderabad","Kolkata","Pune","Ahmedabad",
  "Jaipur","Lucknow","Chandigarh","Surat","Indore","Bhopal","Nagpur","Patna",
  "Vadodara","Coimbatore","Kochi","Visakhapatnam","Noida","Gurgaon","Faridabad",
  "Ghaziabad","Meerut","Agra","Varanasi","Ludhiana","Amritsar","Jodhpur",
  "Ranchi","Raipur","Dehradun","Mysore","Mangalore","Mohali","Panchkula",
  "Zirakpur","Jalandhar","Patiala","Ambala","Karnal","Rohtak","Hisar",
];

const SRC_COLOR = {"Google Maps":"#4285f4","JustDial":"#e8441a","IndiaMart":"#ff6600","Sulekha":"#00a651","LinkedIn":"#0a66c2","Naukri":"#d4291b","YellowPages":"#f5c518"};
const intentStyle = {
  hot:  { bg:"#00d4aa18", color:"#00d4aa", border:"#00d4aa44" },
  warm: { bg:"#f59e0b18", color:"#f59e0b", border:"#f59e0b44" },
  cold: { bg:"#f8717118", color:"#f87171", border:"#f8717144" },
};
const scoreColor = s => s >= 75 ? "#00d4aa" : s >= 50 ? "#f59e0b" : "#f87171";

const S = {
  inp: { width:"100%", background:"#050510", border:"1px solid #ffffff22", borderRadius:10, padding:"12px 14px", color:"#fff", fontFamily:"'DM Sans',sans-serif", fontSize:14, boxSizing:"border-box", outline:"none" },
  lbl: { display:"block", color:"#9ca3af", fontSize:11, marginBottom:6, textTransform:"uppercase", letterSpacing:1 },
};

export default function Search() {
  const [niche, setNiche]     = useState("");
  const [city, setCity]       = useState("");
  const [custom, setCustom]   = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError]     = useState("");
  const [showF, setShowF]     = useState(false);
  const [minScore, setMinScore] = useState(0);
  const [minRating, setMinRating] = useState(0);
  const [intentF, setIntentF] = useState("all");
  const [hasPhone, setHasPhone] = useState(false);
  const [minRev, setMinRev]   = useState(0);
  const [sortBy, setSortBy]   = useState("score");
  const [text, setText]       = useState("");
  const [meta, setMeta]       = useState({});
  const navigate = useNavigate();

  const doSearch = async () => {
    const q = custom.trim() || niche;
    if (!q || !city) { setError("Please select a niche and city."); return; }
    setError(""); setLoading(true); setResults([]); setSearched(false);
    try {
      // Try premium search first, fall back to free search
      let res;
      try {
        res = await API.post("/search-leads", { niche: q, city, max_results: 30 });
        const data = res.data;
        const leads = Array.isArray(data) ? data : (data.leads || []);
        setResults(leads);
        setMeta({
          total: data.total || leads.length,
          locked: 0,
          plan: data.plan || "paid",
          searches_left: data.searches_left !== undefined ? data.searches_left : 999,
          searches_used: data.searches_used || 0,
          monthly_limit: data.monthly_limit || 999,
        });
        setIsPremium(true);
      } catch (premiumErr) {
        if (premiumErr?.response?.status === 403) {
          // Free user - try free search
          try {
            res = await API.post("/free-search", { niche: q, city });
            const leads = res.data.leads || [];
            setResults(leads);
            setMeta({
              total: leads.length,
              locked: 0,
              plan: "free",
              searches_left: res.data.free_remaining || 0,
              free_used: res.data.free_used || 0
            });
          } catch (freeErr) {
            if (freeErr?.response?.status === 403) {
              setError("free_limit");
            } else {
              const msg = freeErr?.response?.data?.detail || "Search failed.";
              setError(typeof msg === "string" ? msg : "Search failed.");
            }
          }
        } else {
          const msg = premiumErr?.response?.data?.detail || "Search failed.";
          setError(typeof msg === "string" ? msg : "Search failed.");
        }
      }
      setSearched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Search failed.";
      setError(typeof msg === "string" ? msg : "Search failed.");
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    let r = [...results];
    if (intentF !== "all") r = r.filter(l => l.intent === intentF);
    if (hasPhone) r = r.filter(l => l.phone);
    if (minScore > 0) r = r.filter(l => (l.score||0) >= minScore);
    if (minRating > 0) r = r.filter(l => (l.rating||0) >= minRating);
    if (minRev > 0) r = r.filter(l => (l.reviews||0) >= minRev);
    if (text) r = r.filter(l => (l.name||"").toLowerCase().includes(text.toLowerCase()) || (l.address||"").toLowerCase().includes(text.toLowerCase()));
    r.sort((a,b) => {
      if (sortBy==="score")   return (b.score||0)-(a.score||0);
      if (sortBy==="rating")  return (b.rating||0)-(a.rating||0);
      if (sortBy==="reviews") return (b.reviews||0)-(a.reviews||0);
      return (a.name||"").localeCompare(b.name||"");
    });
    return r;
  }, [results,intentF,hasPhone,minScore,minRating,minRev,text,sortBy]);

  const exportCSV = () => {
    if (!isPremium) {
      if (window.confirm("CSV export requires a paid plan. Go to upgrade page?")) {
        window.location.href = "/payment";
      }
      return;
    }
    const data = filtered.length > 0 ? filtered : results;
    if (!data || data.length === 0) {
      alert("No leads to export. Search for leads first.");
      return;
    }
    const rows = [["Name","Phone","Address","Rating","Reviews","Score","Intent","Source","Niche","City","Website"]];
    data.forEach(l => rows.push([
      l.name||"", l.phone||"", l.address||"",
      l.rating||"", l.total_ratings||"", l.score||"",
      l.intent||"", l.source||"", l.niche||"", l.city||"", l.website||""
    ]));
    const csv = rows.map(r => r.map(v => '"' + String(v||"").replace(/"/g, '""') + '"').join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `leadbot_leads_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#050510", color:"#fff", fontFamily:"'DM Sans',sans-serif", padding:28 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Syne:wght@700;900&display=swap" rel="stylesheet" />
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.lcard{animation:fadeIn 0.3s ease both}.lcard:hover{border-color:#ffffff22!important;transform:translateY(-1px)}select option{background:#0d0d1f;color:#fff}`}</style>

      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28 }}>
          <div>
            <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:26, fontWeight:900, margin:0 }}>🔍 Lead Search</h1>
            <p style={{ color:"#6b7280", fontSize:13, margin:"4px 0 0" }}>Real businesses · Google Maps · JustDial · IndiaMart · Sulekha</p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            {searched && filtered.length > 0 && (
              <button onClick={exportCSV} style={{ background:"#00d4aa22", color:"#00d4aa", border:"1px solid #00d4aa44", borderRadius:10, padding:"9px 16px", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>⬇ Export CSV</button>
            )}
            <button onClick={() => navigate("/dashboard")} style={{ background:"transparent", color:"#9ca3af", border:"1px solid #ffffff22", borderRadius:10, padding:"9px 16px", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>← Dashboard</button>
          </div>
        </div>

        {/* Search box */}
        <div style={{ background:"linear-gradient(135deg,#0d0d1f,#0f0f22)", border:"1px solid #ffffff0d", borderRadius:20, padding:24, marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>
            <div>
              <label style={S.lbl}>Industry / Niche</label>
              <select value={niche} onChange={e => { setNiche(e.target.value); setCustom(""); }} style={{ ...S.inp, color: niche ? "#fff" : "#6b7280" }}>
                <option value="">Select niche...</option>
                {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>City</label>
              <select value={city} onChange={e => setCity(e.target.value)} style={{ ...S.inp, color: city ? "#fff" : "#6b7280" }}>
                <option value="">Select city...</option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Or Type Custom Niche</label>
              <input placeholder="e.g. Solar dealers, Pet clinics..." value={custom} onChange={e => { setCustom(e.target.value); if(e.target.value) setNiche(""); }} style={S.inp} />
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto auto auto auto", gap:12, alignItems:"flex-end" }}>
            <div>
              <label style={S.lbl}>Search within results</label>
              <input placeholder="Filter by name or address..." value={text} onChange={e => setText(e.target.value)} style={S.inp} />
            </div>
            <div>
              <label style={S.lbl}>Sort</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.inp, width:"auto" }}>
                <option value="score">AI Score</option>
                <option value="rating">Rating</option>
                <option value="reviews">Reviews</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
            <div>
              <label style={S.lbl}>Intent</label>
              <select value={intentF} onChange={e => setIntentF(e.target.value)} style={{ ...S.inp, width:"auto" }}>
                <option value="all">All</option>
                <option value="hot">🔥 Hot</option>
                <option value="warm">👍 Warm</option>
                <option value="cold">❄️ Cold</option>
              </select>
            </div>
            <button onClick={() => setShowF(f => !f)} style={{ background:"#ffffff0d", color:"#9ca3af", border:"1px solid #ffffff22", borderRadius:10, padding:"12px 18px", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", alignSelf:"flex-end" }}>
              ⚙ Filters {showF ? "▲" : "▼"}
            </button>
            <button onClick={doSearch} disabled={loading} style={{ background: loading ? "#1a1a2e" : "linear-gradient(135deg,#00d4aa,#00b894)", color: loading ? "#6b7280" : "#050510", border:"none", borderRadius:10, padding:"12px 28px", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:15, cursor: loading ? "not-allowed" : "pointer", alignSelf:"flex-end", whiteSpace:"nowrap" }}>
              {loading ? <span style={{ display:"inline-block", animation:"spin 0.8s linear infinite" }}>⚡</span> : "🚀 Search"}
            </button>
          </div>

          {error && error !== "free_limit" && <div style={{ background:"#f8717122", border:"1px solid #f87171", borderRadius:10, padding:"10px 16px", marginTop:14, color:"#f87171", fontSize:13 }}>⚠ {error}</div>}
          {error === "free_limit" && (
            <div style={{ background:"#f59e0b18", border:"1px solid #f59e0b44", borderRadius:12, padding:"16px 20px", marginTop:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ color:"#f59e0b", fontWeight:700, marginBottom:4 }}>🔒 Free searches used up</div>
                <div style={{ color:"#9ca3af", fontSize:13 }}>Upgrade to get unlimited searches with real phone numbers</div>
              </div>
              <button onClick={() => navigate("/payment")} style={{ background:"linear-gradient(135deg,#00d4aa,#00b894)", color:"#050510", border:"none", borderRadius:10, padding:"10px 20px", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, whiteSpace:"nowrap" }}>
                Upgrade Now →
              </button>
            </div>
          )}
        </div>

        {/* Advanced filters */}
        {showF && (
          <div style={{ background:"#0d0d1f", border:"1px solid #ffffff0d", borderRadius:16, padding:20, marginBottom:16, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
            <div>
              <label style={S.lbl}>Min AI Score</label>
              <div style={{ display:"flex", gap:6 }}>
                {[[0,"Any"],[50,"50+"],[70,"70+"],[80,"80+"]].map(([v,l]) => (
                  <button key={v} onClick={() => setMinScore(v)} style={{ flex:1, background: minScore===v ? "#00d4aa22" : "transparent", color: minScore===v ? "#00d4aa" : "#6b7280", border:"1px solid "+(minScore===v?"#00d4aa44":"#ffffff11"), borderRadius:8, padding:"6px 0", fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={S.lbl}>Min Rating</label>
              <div style={{ display:"flex", gap:6 }}>
                {[[0,"Any"],[3,"3+"],[4,"4+"],[4.5,"4.5+"]].map(([v,l]) => (
                  <button key={v} onClick={() => setMinRating(v)} style={{ flex:1, background: minRating===v ? "#f59e0b22" : "transparent", color: minRating===v ? "#f59e0b" : "#6b7280", border:"1px solid "+(minRating===v?"#f59e0b44":"#ffffff11"), borderRadius:8, padding:"6px 0", fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={S.lbl}>Filters</label>
              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                  <input type="checkbox" checked={hasPhone} onChange={e => setHasPhone(e.target.checked)} style={{ accentColor:"#00d4aa", width:15, height:15 }} />
                  <span style={{ color:"#9ca3af", fontSize:13 }}>📞 Has Phone</span>
                </label>
                <select value={minRev} onChange={e => setMinRev(Number(e.target.value))} style={{ ...S.inp, width:"auto", padding:"8px 10px" }}>
                  <option value={0}>Any reviews</option>
                  <option value={10}>10+ reviews</option>
                  <option value={50}>50+ reviews</option>
                  <option value={100}>100+ reviews</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ background:"#0d0d1f", border:"1px solid #ffffff0d", borderRadius:20, padding:60, textAlign:"center" }}>
            <div style={{ fontSize:48, display:"inline-block", animation:"spin 1s linear infinite", marginBottom:16 }}>⚡</div>
            <div style={{ color:"#9ca3af", fontSize:15 }}>Searching Google Maps, JustDial, IndiaMart, Sulekha...</div>
            <div style={{ color:"#6b7280", fontSize:13, marginTop:8 }}>This may take 15-30 seconds for real data</div>
          </div>
        )}

        {/* Results */}
        {searched && !loading && (
          <div>
            {/* Summary bar */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <span style={{ color:"#9ca3af", fontSize:14 }}>
                  <span style={{ color:"#00d4aa", fontWeight:700, fontSize:18 }}>{filtered.length}</span> leads shown
                  {meta.total > filtered.length && <span> of {meta.total} found</span>}
                </span>
                {["hot","warm","cold"].map(t => (
                  <span key={t} style={{ background: intentStyle[t].bg, color: intentStyle[t].color, border:"1px solid "+intentStyle[t].border, borderRadius:8, padding:"3px 10px", fontSize:12, fontWeight:700 }}>
                    {t==="hot"?"🔥":t==="warm"?"👍":"❄"} {results.filter(l=>l.intent===t).length}
                  </span>
                ))}
              </div>
              {meta.searches_left !== undefined && (
                <span style={{ color:"#6b7280", fontSize:13 }}>{meta.searches_left} searches left</span>
              )}
            </div>

            {filtered.length === 0 ? (
              <div style={{ background:"#0d0d1f", border:"1px solid #ffffff0d", borderRadius:20, padding:52, textAlign:"center", color:"#6b7280" }}>
                <div style={{ fontSize:36, marginBottom:12 }}>🔍</div>
                No leads match your filters. Try relaxing them.
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {filtered.map((lead, i) => {
                  const ist = intentStyle[lead.intent] || intentStyle.warm;
                  const sc  = SRC_COLOR[lead.source] || "#9ca3af";
                  return (
                    <div key={i} className="lcard" style={{ background:"linear-gradient(135deg,#0d0d1f,#111128)", border:"1px solid #ffffff0a", borderRadius:16, padding:"18px 20px", display:"grid", gridTemplateColumns:"56px 1fr auto", gap:16, alignItems:"start", transition:"all 0.2s", animationDelay:`${Math.min(i,20)*0.03}s` }}>
                      {/* Score */}
                      <div style={{ width:56, height:56, borderRadius:14, background: scoreColor(lead.score||0)+"15", border:"2px solid "+scoreColor(lead.score||0)+"55", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, color: scoreColor(lead.score||0), fontSize:20, lineHeight:1 }}>{lead.score||"?"}</div>
                        <div style={{ fontSize:8, color:"#6b7280", marginTop:2, letterSpacing:0.5 }}>SCORE</div>
                      </div>
                      {/* Info */}
                      <div>
                        <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{lead.name}</div>
                        <div style={{ display:"flex", gap:16, fontSize:13, flexWrap:"wrap", marginBottom:6 }}>
                          {lead.phone   && <span style={{ color:"#00d4aa", fontWeight:600 }}>📞 {lead.phone}</span>}
                          {lead.email   && <span style={{ color:"#a855f7" }}>✉ {lead.email}</span>}
                          {lead.address && <span style={{ color:"#9ca3af" }}>📍 {lead.address}</span>}
                        </div>
                        <div style={{ display:"flex", gap:12, fontSize:12, flexWrap:"wrap", alignItems:"center" }}>
                          {lead.rating > 0 && (
                            <span style={{ color:"#f59e0b" }}>⭐ {lead.rating} <span style={{ color:"#6b7280" }}>({lead.reviews?.toLocaleString()} reviews)</span></span>
                          )}
                          {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" style={{ color:"#60a5fa", textDecoration:"none", fontWeight:600 }}>🌐 Website</a>}
                          {lead.category && <span style={{ color:"#6b7280" }}>📂 {lead.category}</span>}
                        </div>
                      </div>
                      {/* Badges */}
                      <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end" }}>
                        <span style={{ background: sc+"22", color: sc, border:"1px solid "+sc+"44", borderRadius:8, padding:"4px 12px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>{lead.source}</span>
                        <span style={{ background: ist.bg, color: ist.color, border:"1px solid "+ist.border, borderRadius:8, padding:"4px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase" }}>{lead.intent||"warm"}</span>
                        {(lead.score||0) >= 80 && <span style={{ background:"#00d4aa15", color:"#00d4aa", border:"1px solid #00d4aa44", borderRadius:6, padding:"2px 8px", fontSize:10, fontWeight:700 }}>★ HIGH VALUE</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Locked banner */}
            {meta.locked > 0 && (
              <div style={{ background:"linear-gradient(135deg,#0d0d1f,#0f0f22)", border:"2px solid #00d4aa33", borderRadius:16, padding:32, marginTop:16, textAlign:"center" }}>
                <div style={{ fontSize:32, marginBottom:10 }}>🔒</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:20, fontWeight:900, marginBottom:8 }}>{meta.locked} more leads locked</div>
                <p style={{ color:"#9ca3af", fontSize:14, marginBottom:20 }}>Upgrade to unlock all leads with phone numbers and get unlimited searches.</p>
                <button onClick={() => window.location.href="/payment"} style={{ background:"linear-gradient(135deg,#00d4aa,#00b894)", color:"#050510", border:"none", borderRadius:12, padding:"13px 32px", fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer" }}>🚀 Upgrade Now</button>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!searched && !loading && (
          <div style={{ background:"#0d0d1f", border:"1px solid #ffffff0d", borderRadius:20, padding:60, textAlign:"center" }}>
            <div style={{ fontSize:52, marginBottom:16 }}>🎯</div>
            <h3 style={{ fontFamily:"'Syne',sans-serif", fontSize:22, marginBottom:10 }}>Find Real Business Leads</h3>
            <p style={{ color:"#9ca3af", fontSize:15, maxWidth:480, margin:"0 auto" }}>Select a niche and city above to get real businesses with phone numbers from Google Maps, JustDial, IndiaMart and Sulekha — AI scored and ranked.</p>
          </div>
        )}
      </div>
    </div>
  );
}
